import { useState } from 'react';
import { IconButton, ListItemIcon, ListItemText, Menu, MenuItem } from '@mui/material';
import { DarkModeIcon, LightModeIcon, SystemModeIcon } from './icons';
import { useTranslation } from 'react-i18next';
import { useThemeMode, type ThemeModePreference } from '@/context/theme-mode-context';

const OPTIONS: ReadonlyArray<{ value: ThemeModePreference; Icon: typeof LightModeIcon }> = [
  { value: 'light', Icon: LightModeIcon },
  { value: 'dark', Icon: DarkModeIcon },
  { value: 'system', Icon: SystemModeIcon },
];

/**
 * Theme as an icon button with a small menu (TMMAPP-0036) — the compact header form of
 * `ThemeModeSelect`; the icon shows the current preference, which persists the same way.
 */
export function ThemeModeMenuButton() {
  const { t } = useTranslation();
  const { themeMode, setThemeMode } = useThemeMode();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const CurrentIcon = OPTIONS.find((o) => o.value === themeMode)?.Icon ?? SystemModeIcon;

  return (
    <>
      <IconButton
        color="inherit"
        aria-label={t('header.theme')}
        aria-haspopup="menu"
        aria-expanded={anchor !== null}
        onClick={(event) => setAnchor(event.currentTarget)}
      >
        <CurrentIcon size={20} />
      </IconButton>
      <Menu
        anchorEl={anchor}
        open={anchor !== null}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {OPTIONS.map(({ value, Icon }) => (
          <MenuItem
            key={value}
            role="menuitemradio"
            aria-checked={value === themeMode}
            selected={value === themeMode}
            onClick={() => {
              setAnchor(null);
              setThemeMode(value);
            }}
          >
            <ListItemIcon>
              <Icon size={20} />
            </ListItemIcon>
            <ListItemText>{t(`theme.${value}`)}</ListItemText>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
